import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, X, Activity } from 'lucide-react';

export default function VentureCard({ venture, index }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ delay: Math.min(index * 0.05, 0.3), duration: 0.5 }}
        onClick={() => setIsExpanded(true)}
        className="group cursor-pointer relative overflow-hidden rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:shadow-xl hover:shadow-primary/5">
        
        {/* Image */}
        <div className="relative h-48 overflow-hidden bg-secondary/30">
          <img
            src={venture.image}
            alt={`${venture.name} ${venture.tag.toLowerCase()} by Jotunheims`}
            loading="lazy"
            decoding="async"
            width="400"
            height="192"
            className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700 will-change-transform" />
          
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
          
          {/* Status */}
          <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/60 backdrop-blur-sm border border-border/30">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-inter font-medium tracking-wider uppercase text-muted-foreground">
              {['LevelPass', '49 Option', '4Set AI', 'Kbytes', 'Virtual Cop', 'Game Forge', 'Viral Pulse', 'Eternal Legacy', 'ULS - Universal Line Support', 'MangaCraft', 'Aura Vibes', 'DocFactory'].includes(venture.name) ? 'UNDER DEVELOPMENT' : 'MVP OPERATIONAL'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-inter font-bold text-xl text-foreground group-hover:text-primary transition-colors duration-300">
              {venture.name}
            </h3>
            <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed font-inter line-clamp-3">
            {venture.description}
          </p>
          <div className="mt-4 pt-4 border-t border-border/30 flex items-center gap-2">
            <Activity className="w-3 h-3 text-primary/60" />
            <span className="text-[10px] font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground">
              {venture.tag}
            </span>
          </div>
        </div>
      </motion.div>

      {/* Expanded overlay */}
      <AnimatePresence>
        {isExpanded &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/90 backdrop-blur-xl"
          onClick={() => setIsExpanded(false)}>
          
            <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border border-border/50 bg-card">
            
              <button
              onClick={() => setIsExpanded(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/60 backdrop-blur-sm border border-border/30 flex items-center justify-center hover:border-primary/50 transition-colors">
              
                <X className="w-4 h-4 text-foreground" />
              </button>

              <div className="relative h-56 bg-secondary/30">
                <img src={venture.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
              </div>

              <div className="p-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] font-inter font-medium tracking-[0.2em] uppercase text-muted-foreground">
                    {['LevelPass', '49 Option', '4Set AI', 'Kbytes', 'Virtual Cop', 'Game Forge', 'Viral Pulse', 'Eternal Legacy', 'ULS - Universal Line Support', 'MangaCraft', 'Aura Vibes', 'DocFactory'].includes(venture.name) ? 'SYSTEM UNDER DEVELOPMENT' : 'MVP OPERATIONAL'}
                  </span>
                </div>
                <h2 className="font-inter font-black text-3xl text-foreground mb-4">{venture.name}</h2>
                <p className="text-muted-foreground leading-relaxed font-inter mb-6">{venture.fullDescription}</p>
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary/50 border border-border/30 w-fit">
                  <Activity className="w-3 h-3 text-primary" />
                  <span className="text-xs font-inter font-medium tracking-wider uppercase text-foreground">
                    {venture.tag}
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>
    </>);

}